/**
 * Token 工具函数
 * 用于检查 token 是否即将过期，以及刷新 token
 */

/**
 * 检查 token 是否即将过期（在 5 分钟内过期）
 * @param {string} token - JWT token
 * @returns {boolean} - 如果即将过期返回 true
 */
export function isTokenExpiringSoon(token) {
  if (!token) return true;
  
  try {
    // JWT token 由三部分组成：header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    
    // 解码 payload（base64）
    const payload = JSON.parse(atob(parts[1]));
    
    // 检查是否有过期时间
    if (!payload.exp) return true;
    
    // 获取过期时间（秒）和当前时间（秒）
    const expTime = payload.exp;
    const currentTime = Math.floor(Date.now() / 1000);
    
    // 计算剩余时间（秒）
    const timeRemaining = expTime - currentTime;
    
    // 如果剩余时间少于 5 分钟（300 秒），则认为即将过期
    return timeRemaining < 300;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
}

/**
 * 刷新 token
 * @param {Function} axiosInstance - axios 实例（避免循环依赖）
 * @returns {Promise<string|null>} - 新的 token，如果刷新失败返回 null
 */
export async function refreshToken(axiosInstance = null) {
  const currentToken = localStorage.getItem('jwtToken');
  if (!currentToken) {
    return null;
  }
  
  try {
    // 如果提供了 axios 实例，使用它；否则使用原生 fetch（避免循环依赖）
    let response;
    if (axiosInstance) {
      response = await axiosInstance().post('/user/refresh_token', {
        token: currentToken
      });
    } else {
      // 使用原生 fetch 避免循环依赖
      const fetchResponse = await fetch('/user/refresh_token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: currentToken })
      });
      response = { data: await fetchResponse.json() };
    }
    
    if (response.data && response.data.access_token) {
      const newToken = response.data.access_token;
      localStorage.setItem('jwtToken', newToken);
      return newToken;
    }
    
    return null;
  } catch (error) {
    console.error('Token refresh failed:', error);
    // 刷新失败，清除 token
    localStorage.removeItem('jwtToken');
    return null;
  }
}

/**
 * 检查并刷新 token（如果需要）
 * @param {Function} axiosInstance - axios 实例（可选，避免循环依赖）
 * @returns {Promise<string|null>} - 当前有效的 token
 */
export async function checkAndRefreshToken(axiosInstance = null) {
  const currentToken = localStorage.getItem('jwtToken');
  
  if (!currentToken) {
    return null;
  }
  
  // 如果 token 即将过期，尝试刷新
  if (isTokenExpiringSoon(currentToken)) {
    console.log('Token is expiring soon, refreshing...');
    const newToken = await refreshToken(axiosInstance);
    return newToken || currentToken; // 如果刷新失败，返回旧 token（让 API 调用决定如何处理）
  }
  
  return currentToken;
}

